import React, { useContext, useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Table } from 'react-bootstrap';
import { ChevronBarContract, ChevronBarExpand, ChevronDown, ChevronRight, Sliders, SortDown, SortUp } from 'react-bootstrap-icons';
import { AuthContext } from '../contexts/AuthContext';
import { TradingContext } from '../contexts/TradingContext';
import { StreamsContext } from '../contexts/StreamsContext';
import { AppStateContext } from '../contexts/AppStateContext';
import { useGetTicker } from '../hooks/useTradeStation';
import usePositionCalculations from '../hooks/usePositionCalculations';
import { useRelativeStrength } from '../hooks/useRelativeStrength';
import { useTheme } from '../contexts/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import LoadingSpinner from './shared/LoadingSpinner';
import ThemedSelect from './shared/ThemedSelect';
import DashboardSectionTitle from './shared/DashboardSectionTitle';
import BottomSheet from './shared/BottomSheet';
import SheetOptionList from './shared/SheetOptionList';
import PositionMiniChart from './StockChart/PositionMiniChart';
import ScrollPickerSelector from './shared/ScrollPickerSelector';
import { StrengthScanMeter } from './MarketOverview/StrengthOdometer';
import {
  POSITION_CHART_TIMEFRAMES,
  DEFAULT_POSITION_CHART_TIMEFRAME,
} from '../constants/positionChartTimeframes';
import {
  DEFAULT_RELATIVE_STRENGTH_SETTINGS,
  normalizeRelativeStrengthSettings,
  RELATIVE_STRENGTH_SETTINGS_KEY,
} from '../constants/relativeStrengthSettings';
import { isDashboardPath, navigateToTradeWithSymbol, canOpenTradeWithSymbol } from '../utils/tradeNavigation';
import { canAccessTradeWorkspace } from '../utils/brokerConnection';
import { formatSignedCurrency, formatSignedPercent } from '../utils/plFormat';
import { positionOpenedAt, positionOpenedMs } from '../utils/canonicalFields';
import { relativeStrengthScore } from '../utils/relativeStrength';

const positionCardKey = (position, index) =>
  position.PositionKey || position.PositionID || `fallback-${index}`;

const POSITIONS_MOBILE_LAYOUT_KEY = 'positionsMobileLayout';
const POSITIONS_COLUMN_ORDER_KEY = 'positionsColumnOrder';
const DEFAULT_COLUMN_ORDER = [
  'Symbol',
  'TodaysProfitLoss',
  'UnrealizedProfitLoss',
  'Quantity',
  'AvgPrice',
  'PctOfAcct',
  'MarketValue',
  'Opened',
];
const OPTIONAL_COLUMNS = ['RelativeStrength'];
const ALL_KNOWN_COLUMNS = [...DEFAULT_COLUMN_ORDER, ...OPTIONAL_COLUMNS];

function mergePositionsColumnOrder(saved) {
  if (!Array.isArray(saved) || saved.length === 0) return DEFAULT_COLUMN_ORDER;
  const known = new Set(ALL_KNOWN_COLUMNS);
  const kept = saved.filter((key) => known.has(key));
  const missingRequired = DEFAULT_COLUMN_ORDER.filter((key) => !kept.includes(key));
  return [...kept, ...missingRequired];
}

function rsTimeframeFromSettings(settings) {
  if (settings.mode === 'single') return settings.defaultTimeframe;
  if (settings.multiTimeframes.includes('today')) return 'today';
  return settings.multiTimeframes[0] || settings.defaultTimeframe;
}

function rsReadingForSymbol(rsBySymbol, rsLoading, symbol) {
  const entry = rsBySymbol.get(String(symbol || '').trim().toUpperCase());
  if (entry) return entry;
  return { score100: null, loading: rsLoading };
}

function PositionRelativeStrengthMeter({ symbol, rsBySymbol, rsLoading, compact = false }) {
  const reading = rsReadingForSymbol(rsBySymbol, rsLoading, symbol);
  return (
    <div
      style={{
        width: '100%',
        maxWidth: compact ? 72 : 88,
        marginLeft: compact ? 0 : 'auto',
      }}
    >
      <StrengthScanMeter
        testId={`position-rs-${symbol}`}
        score100={reading.score100}
        loading={reading.loading && !Number.isFinite(reading.score100)}
      />
    </div>
  );
}

function formatOpenedTimestamp(raw) {
  if (raw == null || raw === '') return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function isNewestFirstSortField(field) {
  return field === 'TodaysProfitLoss'
    || field === 'UnrealizedProfitLoss'
    || field === 'Opened'
    || field === 'RelativeStrength';
}


const POSITIONS_EXPANDED_CARDS_KEY = 'positionsExpandedCards';
const POSITIONS_CHART_TIMEFRAME_KEY = 'positionsChartTimeframe';


function readExpandedCardKeys() {
  try {
    const raw = sessionStorage.getItem(POSITIONS_EXPANDED_CARDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch (_) {
    return [];
  }
}

function writeExpandedCardKeys(keys) {
  try {
    sessionStorage.setItem(POSITIONS_EXPANDED_CARDS_KEY, JSON.stringify([...keys]));
  } catch (_) {}
}

function readPositionChartTimeframe() {
  try {
    const raw = localStorage.getItem(POSITIONS_CHART_TIMEFRAME_KEY);
    if (POSITION_CHART_TIMEFRAMES.some((tf) => tf.value === raw)) return raw;
  } catch (_) {}
  return DEFAULT_POSITION_CHART_TIMEFRAME;
}

/** Square with two horizontal rules → three stacked sections (compact cards). */
const PositionsCardLayoutIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={{ display: 'block' }}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3 9h18M3 15h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" />
  </svg>
);

/** Four evenly spaced column dividers (five equal sections); rotated 90° → four horizontal rules. */
const PositionsTableLayoutIcon = ({ size = 20 }) => {
  const x0 = 3;
  const w = 18;
  const n = 5;
  const xs = [1, 2, 3, 4].map((k) => x0 + (k * w) / n);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: 'block' }}
    >
      <g transform="rotate(90 12 12)">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path
          d={xs.map((x) => `M${x} 3v18`).join('')}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        />
      </g>
    </svg>
  );
};

const PositionsTable = ({
  renderAsChild = false,
  embedded = false,
  showMobileSectionTitle = false,
  enableLayoutControls = false,
}) => {
  const isMobile = useIsMobile(768, 1024);
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const onDashboard = isDashboardPath(location.pathname);
  const { positions, isLoading, positionsLoading, setSelectedTickerDetails, balances, selectedAccount } = useContext(TradingContext);
  const canActivateSymbol = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const showPresentationControls = isMobile || enableLayoutControls;
  const symbolLinksToTrade = onDashboard && canOpenTradeWithSymbol(user, selectedAccount);
  const symbolOpensChart = !onDashboard && canActivateSymbol;
  const { setMarketAggregates } = useContext(StreamsContext);
  const { activeChartId, updateChartSettings } = useContext(AppStateContext);
  const { getTicker } = useGetTicker();
  const { calculateUnrealizedPL, calculateUnrealizedPLPercent, calculateMarketValue } = usePositionCalculations();
  
  // Note: Positions are now guaranteed to be deduplicated by the centralized PositionsManager
  // No need for additional deduplication here

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sortField, setSortField] = useState('UnrealizedProfitLoss');
  const [sortDirection, setSortDirection] = useState('desc');
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(POSITIONS_COLUMN_ORDER_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return mergePositionsColumnOrder(parsed);
    } catch (_) {
      return DEFAULT_COLUMN_ORDER;
    }
  });
  const [draggingCol, setDraggingCol] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragInsertSide, setDragInsertSide] = useState('left'); // 'left' | 'right'
  useEffect(() => {
    try { localStorage.setItem(POSITIONS_COLUMN_ORDER_KEY, JSON.stringify(columnOrder)); } catch (_) {}
  }, [columnOrder]);

  const [mobileLayout, setMobileLayout] = useState(() => {
    try {
      const raw = localStorage.getItem(POSITIONS_MOBILE_LAYOUT_KEY);
      if (raw === 'table' || raw === 'compact') return raw;
    } catch (_) {}
    return 'compact';
  });
  useEffect(() => {
    try {
      localStorage.setItem(POSITIONS_MOBILE_LAYOUT_KEY, mobileLayout);
    } catch (_) {}
  }, [mobileLayout]);

  const applyPositionsSortField = useCallback((field) => {
    setSortField(field);
    setSortDirection(isNewestFirstSortField(field) ? 'desc' : 'asc');
  }, []);

  const showRsColumn = columnOrder.includes('RelativeStrength');
  const [columnsSheetOpen, setColumnsSheetOpen] = useState(false);
  const controlsRef = useRef(null);
  const [controlsHeight, setControlsHeight] = useState(0);
  const positionTickers = useMemo(
    () => [...new Set(
      (positions || [])
        .map((p) => String(p.Symbol || '').trim().toUpperCase())
        .filter(Boolean),
    )],
    [positions],
  );
  const rsSettings = useMemo(
    () => normalizeRelativeStrengthSettings(
      user?.appSettings?.[RELATIVE_STRENGTH_SETTINGS_KEY] || DEFAULT_RELATIVE_STRENGTH_SETTINGS,
    ),
    [user?.appSettings],
  );
  const { rows: rsRows, loading: rsLoading } = useRelativeStrength({
    tickers: positionTickers,
    relativeTo: 'SPY',
    timeframeKey: rsTimeframeFromSettings(rsSettings),
    enabled: showRsColumn && positionTickers.length > 0,
  });
  const rsBySymbol = useMemo(() => {
    const map = new Map();
    (rsRows || []).forEach((row) => {
      map.set(row.ticker, {
        score100: relativeStrengthScore(row.scorePct),
        loading: !!row.loading,
      });
    });
    return map;
  }, [rsRows]);

  useEffect(() => {
    if (!showRsColumn && sortField === 'RelativeStrength') {
      applyPositionsSortField('UnrealizedProfitLoss');
    }
  }, [showRsColumn, sortField, applyPositionsSortField]);

  const toggleRelativeStrengthColumn = useCallback(() => {
    setColumnOrder((prev) => (
      prev.includes('RelativeStrength')
        ? prev.filter((key) => key !== 'RelativeStrength')
        : [...prev, 'RelativeStrength']
    ));
  }, []);

  const formatCurrency = formatSignedCurrency;

  const formatPercentage = formatSignedPercent;

  const formatPortfolioPercentage = (value) => {
    if (!value) return '0.00%';
    const num = parseFloat(value);
    return `${num.toFixed(2)}%`;
  };

  const calculateTodayPercentage = (todaysProfitLoss, lastPrice, quantity, markToMarketPrice) => {
    if (!todaysProfitLoss || !quantity) return 0;
    
    const todaysPL = parseFloat(todaysProfitLoss);
    const price = parseFloat(markToMarketPrice) || parseFloat(lastPrice) || 0;
    const qty = parseFloat(quantity);
    
    if (price === 0) return 0;
    
    // Calculate position value using mark to market price
    // Use absolute value since TradeStation returns negative quantity for short positions
    const positionValue = price * Math.abs(qty);
    
    // Calculate percentage change
    const percentage = (todaysPL / positionValue) * 100;
    
    return percentage;
  };

  const getProfitLossColorValue = (value) => {
    if (!value) return colors.chartNeutral;
    const num = parseFloat(value);
    return num >= 0 ? colors.chartUp : colors.chartDown;
  };

  const handlePositionSymbolClick = useCallback(
    async (position) => {
      // Chart ticker requires an effective MD source (selected or sibling).
      if (canAccessTradeWorkspace(user, selectedAccount) === false) return;

      if (onDashboard) {
        navigateToTradeWithSymbol(navigate, position.Symbol, user, selectedAccount);
        return;
      }

      const tickerDetails = await getTicker(position.Symbol);
      if (!tickerDetails || tickerDetails.Errors?.length || !tickerDetails.Symbols?.length) {
        return;
      }
      if (activeChartId) {
        updateChartSettings(activeChartId, { ticker: position.Symbol });
      }
      setSelectedTickerDetails(tickerDetails.Symbols[0]);
      setMarketAggregates({ bids: [], asks: [] });
      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent('positions:tickerClicked', { detail: { ticker: position.Symbol } })
        );
        const handleChartTabReady = () => {
          const attemptScroll = (attempts = 0) => {
            const chartSection = document.getElementById('mobile-chart-section');
            if (chartSection) {
              const rect = chartSection.getBoundingClientRect();
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              const targetY = rect.top + scrollTop - 10;
              window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
              window.removeEventListener('positions:chartTabReady', handleChartTabReady);
            } else if (attempts < 20) {
              setTimeout(() => attemptScroll(attempts + 1), 50);
            } else {
              window.removeEventListener('positions:chartTabReady', handleChartTabReady);
            }
          };
          attemptScroll();
        };
        window.addEventListener('positions:chartTabReady', handleChartTabReady, { once: true });
        setTimeout(() => {
          const chartSection = document.getElementById('mobile-chart-section');
          if (chartSection) {
            const rect = chartSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = rect.top + scrollTop - 10;
            window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
          }
        }, 500);
      }
    },
    [activeChartId, getTicker, isMobile, navigate, onDashboard, selectedAccount, setMarketAggregates, setSelectedTickerDetails, updateChartSettings, user]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(isNewestFirstSortField(field) ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <SortUp /> : <SortDown />;
  };

  const headerDragStart = (key) => setDraggingCol(key);
  const headerDragOver = (e, idx) => {
    e.preventDefault();
    try {
      const { offsetX, target } = e.nativeEvent;
      const width = target?.clientWidth || 1;
      setDragInsertSide(offsetX > width / 2 ? 'right' : 'left');
    } catch (_) {
      setDragInsertSide('left');
    }
    setDragOverIdx(idx);
  };
  const headerDrop = (e, idx) => {
    e.preventDefault();
    if (!draggingCol) { setDragOverIdx(null); return; }
    const fromIdx = columnOrder.indexOf(draggingCol);
    let insertIdx = idx + (dragInsertSide === 'right' ? 1 : 0);
    if (fromIdx === -1) { setDraggingCol(null); setDragOverIdx(null); return; }
    if (fromIdx < insertIdx) insertIdx -= 1; // account for removal shift
    const next = [...columnOrder];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(insertIdx, 0, moved);
    setColumnOrder(next);
    setDraggingCol(null);
    setDragOverIdx(null);
    setDragInsertSide('left');
  };

  const renderHeaderCell = (key, idx) => {
    const baseProps = {
      draggable: true,
      onDragStart: () => headerDragStart(key),
      onDragOver: (e) => headerDragOver(e, idx),
      onDrop: (e) => headerDrop(e, idx),
      style: {
        cursor: draggingCol ? 'grabbing' : 'grab',
        fontSize: '0.8rem',
        whiteSpace: 'nowrap',
        borderLeft: dragOverIdx === idx && dragInsertSide === 'left' ? `3px solid ${colors.primary}` : undefined,
        borderRight: dragOverIdx === idx && dragInsertSide === 'right' ? `3px solid ${colors.primary}` : undefined,
        paddingLeft: '6px',
        paddingRight: '6px'
      }
    };
    const labelWrap = (label, onClick, alignRight = false, sortKey = null) => (
      <th key={key} {...baseProps} onClick={onClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: alignRight ? 'flex-end' : 'flex-start' }}>
          <span>{label}</span>
          {onClick && sortKey ? <span>{getSortIcon(sortKey)}</span> : null}
        </div>
      </th>
    );
    if (key === 'Symbol') return labelWrap('Symbol', () => handleSort('Symbol'), false, 'Symbol');
    if (key === 'TodaysProfitLoss') return labelWrap("Day's P&L", () => handleSort('TodaysProfitLoss'), false, 'TodaysProfitLoss');
    if (key === 'UnrealizedProfitLoss') return labelWrap('Unrealized P&L', () => handleSort('UnrealizedProfitLoss'), false, 'UnrealizedProfitLoss');
    if (key === 'Quantity') return labelWrap('Qty', () => handleSort('Quantity'), false, 'Quantity');
    if (key === 'AvgPrice') return labelWrap('Avg Price', () => handleSort('AvgPrice'), true, 'AvgPrice');
    if (key === 'PctOfAcct') return labelWrap('% of Acct', () => handleSort('PctOfAcct'), true, 'PctOfAcct');
    if (key === 'MarketValue') return labelWrap('Market Value', () => handleSort('MarketValue'), true, 'MarketValue');
    if (key === 'Opened') return labelWrap('Opened', () => handleSort('Opened'), false, 'Opened');
    if (key === 'RelativeStrength') return labelWrap('Rel. Strength', () => handleSort('RelativeStrength'), true, 'RelativeStrength');
    return null;
  };

  const renderBodyCell = (key, position, computed) => {
    const { correctUnrealizedPL, correctUnrealizedPLPercent, marketValue, accountPercentage } = computed;
    if (key === 'Symbol') {
      const symbolTitle = symbolLinksToTrade
        ? `Open ${position.Symbol} on Trade`
        : symbolOpensChart
          ? `Click to switch active chart to ${position.Symbol}`
          : position.Symbol;
      const symbolClickable = symbolLinksToTrade || symbolOpensChart;
      const symbolStyle = {
        fontSize: '0.8rem',
        cursor: symbolClickable ? 'pointer' : 'default',
        color: symbolClickable ? colors.primary : colors.text,
        textDecoration: 'none',
        transition: 'color 0.2s ease',
      };
      return (
        <td key={key}>
          <div>
            <strong
              style={symbolStyle}
              onClick={() => handlePositionSymbolClick(position)}
              onMouseEnter={(e) => { if (symbolClickable) e.target.style.color = colors.primary; }}
              onMouseLeave={(e) => { if (symbolClickable) e.target.style.color = colors.primary; }}
              title={symbolTitle}
            >
              {position.Symbol}
            </strong>
            {position.LongShort?.toLowerCase() === 'short' && (
              <span 
                style={{ 
                  marginLeft: '6px', 
                  padding: '2px 6px', 
                  backgroundColor: colors.chartDown, 
                  color: '#FFFFFF', 
                  fontSize: '0.65rem', 
                  fontWeight: 'bold', 
                  borderRadius: '3px',
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              >
                SHORT
              </span>
            )}
          </div>
        </td>
      );
    }
    if (key === 'Quantity') return (<td key={key}><strong style={{ fontSize: '0.8rem' }}>{position.Quantity}</strong></td>);
    if (key === 'TodaysProfitLoss') {
      const pct = calculateTodayPercentage(position.TodaysProfitLoss, position.Last, position.Quantity, position.MarkToMarketPrice);
      return (
        <td key={key}>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: getProfitLossColorValue(pct), fontSize: '0.8rem', opacity: 0.9, whiteSpace: 'nowrap' }}>{formatPercentage(pct)}</span>
              <span style={{ fontSize: '0.8rem', color: getProfitLossColorValue(pct), whiteSpace: 'nowrap' }}>{formatCurrency(position.TodaysProfitLoss)}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: getProfitLossColorValue(pct), whiteSpace: 'nowrap' }}>{formatCurrency(position.TodaysProfitLoss)}</span>
              <span style={{ color: getProfitLossColorValue(pct), fontSize: '0.8rem', opacity: 0.9, whiteSpace: 'nowrap' }}>{formatPercentage(pct)}</span>
            </div>
          )}
        </td>
      );
    }
    if (key === 'UnrealizedProfitLoss') {
      return (
        <td key={key}>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ color: getProfitLossColorValue(correctUnrealizedPLPercent), fontSize: '0.8rem', opacity: 0.9, whiteSpace: 'nowrap' }}>{formatPercentage(correctUnrealizedPLPercent)}</span>
              <span style={{ fontSize: '0.8rem', color: getProfitLossColorValue(correctUnrealizedPLPercent), whiteSpace: 'nowrap' }}>{formatCurrency(correctUnrealizedPL)}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: getProfitLossColorValue(correctUnrealizedPLPercent), whiteSpace: 'nowrap' }}>{formatCurrency(correctUnrealizedPL)}</span>
              <span style={{ color: getProfitLossColorValue(correctUnrealizedPLPercent), fontSize: '0.8rem', opacity: 0.9, whiteSpace: 'nowrap' }}>{formatPercentage(correctUnrealizedPLPercent)}</span>
            </div>
          )}
        </td>
      );
    }
    if (key === 'AvgPrice') return (<td key={key} style={{ textAlign: 'right' }}><div><strong style={{ fontSize: '0.8rem' }}>{formatCurrency(position.AveragePrice).replace(/[+-]/, '')}</strong></div></td>);
    if (key === 'PctOfAcct') return (<td key={key} style={{ textAlign: 'right' }}><div><strong style={{ fontSize: '0.8rem' }}>{formatPortfolioPercentage(accountPercentage)}</strong></div></td>);
    if (key === 'MarketValue') return (<td key={key} style={{ textAlign: 'right' }}><div><strong style={{ fontSize: '0.8rem' }}>{formatCurrency(marketValue).replace(/[+-]/, '')}</strong></div></td>);
    if (key === 'Opened') {
      return (
        <td key={key} style={{ whiteSpace: 'nowrap' }}>
          <strong style={{ fontSize: '0.8rem' }}>{formatOpenedTimestamp(positionOpenedAt(position))}</strong>
        </td>
      );
    }
    if (key === 'RelativeStrength') {
      return (
        <td key={key} style={{ textAlign: 'right' }}>
          <PositionRelativeStrengthMeter
            symbol={position.Symbol}
            rsBySymbol={rsBySymbol}
            rsLoading={rsLoading}
          />
        </td>
      );
    }
    return null;
  };

  // Calculate total account P&L summaries
  const accountSummary = useMemo(() => {
    if (!positions || positions.length === 0) {
      return {
        totalTodayPL: 0,
        totalUnrealizedPL: 0,
        totalPortfolioValue: 0,
        todayPLPercent: 0,
        unrealizedPLPercent: 0
      };
    }

    let totalUnrealizedPL = 0;
    let totalPortfolioValue = 0;
    let totalPositionsTodayPL = 0;

    positions.forEach(position => {
      // Unrealized P&L using correct calculation
      const unrealizedPL = calculateUnrealizedPL(position);
      totalUnrealizedPL += unrealizedPL;

      // Portfolio value for percentage calculations
      const currentPrice = parseFloat(position.Last) || parseFloat(position.MarkToMarketPrice) || 0;
      const quantity = parseFloat(position.Quantity) || 0;
      // Use absolute value since TradeStation returns negative quantity for short positions
      const positionValue = currentPrice * Math.abs(quantity);
      totalPortfolioValue += positionValue;
      
      // Sum position today's P&L (for equity accounts)
      totalPositionsTodayPL += parseFloat(position.TodaysProfitLoss) || 0;
    });

    // Calculate today's P&L based on account type
    const balance = balances?.[0];
    const isFutures = balance?.AccountType === 'Futures';
    const totalTodayPL = isFutures 
      ? (parseFloat(balance?.TodaysProfitLoss) || 0)  // Futures: Use balance
      : totalPositionsTodayPL;  // Equity: Use sum of position TodaysProfitLoss only

    const todayPLPercent = totalPortfolioValue > 0 ? (totalTodayPL / totalPortfolioValue) * 100 : 0;
    const unrealizedPLPercent = totalPortfolioValue > 0 ? (totalUnrealizedPL / totalPortfolioValue) * 100 : 0;

    return {
      totalTodayPL,
      totalUnrealizedPL,
      totalPortfolioValue,
      todayPLPercent,
      unrealizedPLPercent
    };
  }, [positions, balances, calculateUnrealizedPL]);

  const calculateAccountPercentage = useCallback(
    (position) => {
      const marketValue = calculateMarketValue(position);
      return accountSummary.totalPortfolioValue > 0
        ? (marketValue / accountSummary.totalPortfolioValue) * 100
        : 0;
    },
    [accountSummary, calculateMarketValue]
  );

  const sortedPositions = useMemo(() => {
    if (!positions || positions.length === 0) return [];
    
    return [...positions].sort((a, b) => {
      if (sortField === 'RelativeStrength') {
        const aScore = rsReadingForSymbol(rsBySymbol, rsLoading, a.Symbol).score100;
        const bScore = rsReadingForSymbol(rsBySymbol, rsLoading, b.Symbol).score100;
        if (!Number.isFinite(aScore) && !Number.isFinite(bScore)) return 0;
        if (!Number.isFinite(aScore)) return 1;
        if (!Number.isFinite(bScore)) return -1;
        return sortDirection === 'asc' ? aScore - bScore : bScore - aScore;
      }

      let aValue, bValue;
      
      switch (sortField) {
        case 'Symbol':
          aValue = a.Symbol || '';
          bValue = b.Symbol || '';
          break;
        case 'TodaysProfitLoss':
          aValue = parseFloat(a.TodaysProfitLoss) || 0;
          bValue = parseFloat(b.TodaysProfitLoss) || 0;
          break;
        case 'UnrealizedProfitLoss':
          aValue = calculateUnrealizedPL(a);
          bValue = calculateUnrealizedPL(b);
          break;
        case 'Quantity':
          aValue = parseFloat(a.Quantity) || 0;
          bValue = parseFloat(b.Quantity) || 0;
          break;
        case 'AvgPrice': {
          const aAvg = parseFloat(a.AveragePrice) || 0;
          const bAvg = parseFloat(b.AveragePrice) || 0;
          aValue = aAvg; bValue = bAvg;
          break;
        }
        case 'PctOfAcct':
          aValue = calculateAccountPercentage(a);
          bValue = calculateAccountPercentage(b);
          break;
        case 'MarketValue':
          aValue = calculateMarketValue(a);
          bValue = calculateMarketValue(b);
          break;
        case 'Opened':
          aValue = positionOpenedMs(a);
          bValue = positionOpenedMs(b);
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }
      
      let cmp = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        cmp = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (aNum > bNum) cmp = 1;
        else if (aNum < bNum) cmp = -1;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [
    positions,
    sortField,
    sortDirection,
    calculateUnrealizedPL,
    calculateMarketValue,
    calculateAccountPercentage,
    rsBySymbol,
    rsLoading,
  ]);

  const positionsSortOptions = [
    { field: 'Symbol', label: 'Symbol' },
    { field: 'TodaysProfitLoss', label: "Today's P&L" },
    { field: 'UnrealizedProfitLoss', label: 'Overall P&L' },
    { field: 'Quantity', label: 'Qty' },
    { field: 'AvgPrice', label: 'Avg price' },
    { field: 'PctOfAcct', label: '% of account' },
    { field: 'MarketValue', label: 'Market value' },
    { field: 'Opened', label: 'Opened' },
    ...(showRsColumn ? [{ field: 'RelativeStrength', label: 'Relative strength' }] : []),
  ];

  const embeddedSurface = embedded && renderAsChild;
  const rowSurface = embeddedSurface ? colors.bgPrimary : 'transparent';
  const embeddedCardInsetX = 18;

  const embeddedCardShellStyle = {
    // Remove card wrapper - make positions feel part of the page
    backgroundColor: 'transparent',
    overflow: 'visible',
  };

  const [expandedCards, setExpandedCards] = useState(() => new Set(readExpandedCardKeys()));
  const [chartTimeframe, setChartTimeframe] = useState(readPositionChartTimeframe);

  useEffect(() => {
    writeExpandedCardKeys(expandedCards);
  }, [expandedCards]);

  useEffect(() => {
    try {
      localStorage.setItem(POSITIONS_CHART_TIMEFRAME_KEY, chartTimeframe);
    } catch (_) {}
  }, [chartTimeframe]);

  const selectedChartTimeframe = useMemo(
    () => POSITION_CHART_TIMEFRAMES.find((tf) => tf.value === chartTimeframe)
      || POSITION_CHART_TIMEFRAMES.find((tf) => tf.value === 'D'),
    [chartTimeframe],
  );

  const toggleCardExpand = (key) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const anyCardExpanded = expandedCards.size > 0;
  const showChartTimeframePicker = mobileLayout === 'compact' && anyCardExpanded;
  const pinColumnHeader = isMobile && !embeddedSurface;

  // Measure before paint so sticky column headers never sit at top:0 over the first row.
  useLayoutEffect(() => {
    if (!pinColumnHeader) return undefined;
    const el = controlsRef.current;
    if (!el) return undefined;
    const measure = () => setControlsHeight(el.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [pinColumnHeader, mobileLayout, showChartTimeframePicker, showRsColumn]);

  const toggleExpandCollapseAll = useCallback(() => {
    if (anyCardExpanded) {
      setExpandedCards(new Set());
      return;
    }
    setExpandedCards(new Set(sortedPositions.map((p, i) => positionCardKey(p, i))));
  }, [anyCardExpanded, sortedPositions]);

  // Drop expand keys for positions that no longer exist (keeps sticky state accurate).
  useEffect(() => {
    const validKeys = new Set(sortedPositions.map((p, i) => positionCardKey(p, i)));
    setExpandedCards((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (validKeys.has(key)) next.add(key);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [sortedPositions]);

  const toolbarIconButtonStyle = (active = false) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 30,
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    backgroundColor: active ? colors.bgSecondary : 'transparent',
    color: active ? colors.primary : colors.textMuted,
    cursor: 'pointer',
    padding: 0,
  });

  // Layout + expand-all change how the list is presented rather than what it contains,
  // so they sit with the section identity and leave the toolbar row to sorting.
  const renderMobileViewControls = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        title="Columns"
        aria-label="Position columns"
        aria-expanded={columnsSheetOpen}
        aria-pressed={showRsColumn}
        data-testid="positions-columns-button"
        onClick={() => setColumnsSheetOpen(true)}
        style={toolbarIconButtonStyle(showRsColumn)}
      >
        <Sliders size={16} />
      </button>
      {mobileLayout === 'compact' && sortedPositions.length > 0 ? (
        <button
          type="button"
          title={anyCardExpanded ? 'Collapse all' : 'Expand all'}
          aria-label={anyCardExpanded ? 'Collapse all position charts' : 'Expand all position charts'}
          onClick={toggleExpandCollapseAll}
          style={toolbarIconButtonStyle()}
        >
          {anyCardExpanded ? <ChevronBarContract size={16} /> : <ChevronBarExpand size={16} />}
        </button>
      ) : null}
      <div
        role="group"
        aria-label="Positions layout"
        style={{
          display: 'inline-flex',
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          aria-pressed={mobileLayout === 'compact'}
          aria-label="Compact cards"
          onClick={() => setMobileLayout('compact')}
          style={{
            ...toolbarIconButtonStyle(mobileLayout === 'compact'),
            border: 'none',
            borderRadius: 0,
            borderRight: `1px solid ${colors.border}`,
          }}
        >
          <PositionsCardLayoutIcon size={18} />
        </button>
        <button
          type="button"
          aria-pressed={mobileLayout === 'table'}
          aria-label="Full table"
          onClick={() => setMobileLayout('table')}
          style={{
            ...toolbarIconButtonStyle(mobileLayout === 'table'),
            border: 'none',
            borderRadius: 0,
          }}
        >
          <PositionsTableLayoutIcon size={18} />
        </button>
      </div>
    </div>
  );

  const renderMobileSectionHeader = () => (
    <DashboardSectionTitle
      title="Positions"
      count={sortedPositions.length > 0 ? sortedPositions.length : null}
      marginBottom={8}
    />
  );

  const renderMobileToolbar = () => (
    <div
      ref={controlsRef}
      role="toolbar"
      aria-label="Positions view and sort"
      style={{
        position: embeddedSurface ? 'relative' : 'sticky',
        top: embeddedSurface ? undefined : 0,
        zIndex: embeddedSurface ? undefined : 25,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 0,
        marginLeft: embeddedSurface ? 0 : -2,
        marginRight: embeddedSurface ? 0 : -2,
        paddingLeft: embeddedSurface ? embeddedCardInsetX : 4,
        paddingRight: embeddedSurface ? embeddedCardInsetX : 4,
        paddingTop: embeddedSurface ? 8 : 6,
        paddingBottom: embeddedSurface ? 8 : 8,
        flexWrap: 'nowrap',
        backgroundColor: embeddedSurface ? 'transparent' : colors.bgPrimary,
        borderBottom: `1px solid ${colors.border}`,
        boxShadow: embeddedSurface ? 'none' : `0 6px 12px -8px rgba(0,0,0,0.45)`,
      }}
    >
      <div role="group" aria-label="Sort positions by field" style={{ flex: '0 0 auto' }}>
        <ThemedSelect
          value={sortField}
          onChange={(v) => applyPositionsSortField(v)}
          options={positionsSortOptions.map(({ field, label }) => ({ value: field, label }))}
          fitContent
          height={32}
          fontSize="0.8rem"
          fontWeight={600}
          minWidth={0}
          aria-label="Sort positions by"
        />
      </div>
      <button
        type="button"
        title={sortDirection === 'asc' ? 'Ascending — tap for descending' : 'Descending — tap for ascending'}
        aria-label={sortDirection === 'asc' ? 'Sort ascending, switch to descending' : 'Sort descending, switch to ascending'}
        onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary,
          color: colors.textMuted,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {sortDirection === 'asc' ? <SortUp size={16} /> : <SortDown size={16} />}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0, minWidth: 0 }}>
        {showChartTimeframePicker ? (
          <div role="group" aria-label="Chart timeframe" style={{ flexShrink: 0 }}>
            <ScrollPickerSelector
              items={POSITION_CHART_TIMEFRAMES}
              value={selectedChartTimeframe}
              onChange={(tf) => setChartTimeframe(tf.value)}
              renderItem={(item, isSelected) => (
                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: isSelected ? '700' : '500',
                    color: isSelected ? colors.primary : colors.text,
                    textShadow: isSelected ? `0 0 10px ${colors.primary}40` : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {item.label}
                </span>
              )}
              getItemKey={(item) => item.value}
              getItemLabel={(item) => item.label}
              placeholder="1D"
              showDropdown
              ariaLabel="Chart interval"
            />
          </div>
        ) : null}
        {renderMobileViewControls()}
      </div>
    </div>
  );

  // Shared column template: symbol | qty | today | overall | [rs] | chevron
  // Qty track keeps a little right inset so right-aligned counts don't crowd Today.
  const CARD_GRID_COLS = showRsColumn
    ? 'minmax(48px, 0.95fr) minmax(32px, 0.45fr) minmax(52px, 0.85fr) minmax(52px, 0.85fr) minmax(56px, 0.8fr) 24px'
    : 'minmax(56px, 1.1fr) minmax(40px, 0.6fr) 1fr 1fr 24px';
  const CARD_QTY_CELL_STYLE = {
    minWidth: 0,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    paddingRight: 10,
    boxSizing: 'border-box',
  };

  const renderCompactPositionCards = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Column header row */}
      <div
        data-testid="positions-column-header"
        style={{
          display: 'grid',
          gridTemplateColumns: CARD_GRID_COLS,
          gap: '0 8px',
          padding: embeddedSurface
            ? `4px ${embeddedCardInsetX}px 4px`
            : '6px 10px 8px',
          marginBottom: 2,
          borderBottom: `1px solid ${colors.border}`,
          ...(pinColumnHeader ? {
            position: 'sticky',
            // Wait for a real toolbar measurement so we don't pin over row one at top:0.
            top: controlsHeight > 0 ? controlsHeight : undefined,
            zIndex: 20,
            backgroundColor: colors.bgPrimary,
            boxShadow: `0 1px 0 ${colors.border}`,
          } : {}),
        }}
      >
        <span style={{ fontSize: '0.6875rem', color: colors.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Symbol</span>
        <span style={{ fontSize: '0.6875rem', color: colors.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'right', paddingRight: 10, boxSizing: 'border-box' }}>Qty</span>
        <span style={{ fontSize: '0.6875rem', color: colors.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Today</span>
        <span style={{ fontSize: '0.6875rem', color: colors.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Overall</span>
        {showRsColumn ? (
          <span style={{ fontSize: '0.6875rem', color: colors.textMuted, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'right' }}>RS</span>
        ) : null}
        <span />
      </div>

      {sortedPositions.map((position, index) => {
        const correctUnrealizedPL = calculateUnrealizedPL(position);
        const correctUnrealizedPLPercent = calculateUnrealizedPLPercent(position);
        const marketValue = calculateMarketValue(position);
        const accountPercentage = calculateAccountPercentage(position);
        const todayPct = calculateTodayPercentage(
          position.TodaysProfitLoss,
          position.Last,
          position.Quantity,
          position.MarkToMarketPrice
        );
        const todayColor = getProfitLossColorValue(todayPct);
        const overallColor = getProfitLossColorValue(correctUnrealizedPLPercent);
        const isShort = position.LongShort?.toLowerCase() === 'short';
        const absQty = Math.abs(parseFloat(position.Quantity) || 0);
        const qtyUnit = position.AssetType === 'FUTURE' ? 'contracts' : 'shares';
        const uniqueKey = positionCardKey(position, index);
        const isExpanded = expandedCards.has(uniqueKey);
        const isLast = index === sortedPositions.length - 1;
        return (
          <div
            key={uniqueKey}
            style={{
              backgroundColor: embeddedSurface ? colors.bgSecondary : rowSurface,
              padding: embeddedSurface ? `8px ${embeddedCardInsetX}px` : '8px 10px',
              borderRadius: embeddedSurface ? 0 : 6,
              border: embeddedSurface ? 'none' : 'none',
              borderBottom: !isLast ? `1px solid ${colors.border}` : undefined,
            }}
          >
            {/* Main row — tap anywhere (except symbol link) to expand/collapse chart */}
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => toggleCardExpand(uniqueKey)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCardExpand(uniqueKey);
                }
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: CARD_GRID_COLS,
                gap: '4px 8px',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              {/* Symbol + LONG/SHORT badge */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <strong
                  role={symbolLinksToTrade || symbolOpensChart ? 'button' : undefined}
                  tabIndex={symbolLinksToTrade || symbolOpensChart ? 0 : undefined}
                  onClick={(e) => {
                  e.stopPropagation();
                  handlePositionSymbolClick(position);
                  }}
                  onKeyDown={(e) => {
                    if (!(symbolLinksToTrade || symbolOpensChart)) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePositionSymbolClick(position);
                    }
                  }}
                  style={{
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    cursor: symbolLinksToTrade || symbolOpensChart ? 'pointer' : 'default',
                    color: symbolLinksToTrade || symbolOpensChart ? colors.primary : colors.text,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={
                    symbolLinksToTrade
                      ? `Open ${position.Symbol} on Trade`
                      : symbolOpensChart
                        ? `Click to switch active chart to ${position.Symbol}`
                        : position.Symbol
                  }
                >
                  {position.Symbol}
                </strong>
                {/* Direction is neutral metadata — green/red stays reserved for P&L. */}
                <span
                  style={{
                    alignSelf: 'flex-start',
                    color: colors.textMuted,
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    lineHeight: 1.3,
                  }}
                >
                  {isShort ? 'SHORT' : 'LONG'}
                </span>
              </div>

              {/* Qty — always visible in list card mode */}
              <div
                data-testid={`position-card-qty-${position.Symbol}`}
                style={CARD_QTY_CELL_STYLE}
              >
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: colors.text,
                    lineHeight: 1.15,
                  }}
                >
                  {absQty}
                </span>
              </div>

              {/* Today — values only (header row has the label) */}
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: todayColor, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                  {formatPercentage(todayPct)}
                </span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: todayColor, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(position.TodaysProfitLoss)}
                </span>
              </div>

              {/* Overall — values only (header row has the label) */}
              <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: overallColor, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                  {formatPercentage(correctUnrealizedPLPercent)}
                </span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: overallColor, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(correctUnrealizedPL)}
                </span>
              </div>

              {showRsColumn ? (
                <div
                  data-testid={`position-card-rs-${position.Symbol}`}
                  style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                >
                  <PositionRelativeStrengthMeter
                    symbol={position.Symbol}
                    rsBySymbol={rsBySymbol}
                    rsLoading={rsLoading}
                    compact
                  />
                </div>
              ) : null}

              {/* Expand chevron */}
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse chart' : 'Expand chart'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCardExpand(uniqueKey);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: colors.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  transition: 'transform 0.2s ease',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <ChevronDown size={16} />
              </button>
            </div>

            {/* Expandable chart (ticker / P&L already shown in the row) */}
            {isExpanded && (
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <>
                  {canActivateSymbol ? (
                    <PositionMiniChart
                      symbol={position.Symbol}
                      position={position}
                      height={200}
                      hideMeta
                      interval={selectedChartTimeframe.interval}
                      unit={selectedChartTimeframe.unit}
                    />
                  ) : null}
                  <div
                    data-testid={`position-card-details-${position.Symbol}`}
                    style={{
                      marginTop: canActivateSymbol ? 8 : 0,
                      fontSize: '0.6875rem',
                      lineHeight: 1.45,
                      color: colors.textMuted,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px 0',
                    }}
                  >
                    <span>Value {formatCurrency(marketValue).replace(/[+-]/g, '')}</span>
                    <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                    <span>{absQty} {qtyUnit}</span>
                    <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                    <span>Avg {formatCurrency(position.AveragePrice).replace(/[+-]/g, '')}</span>
                    <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                    <span>{formatPortfolioPercentage(accountPercentage)} of acct</span>
                    {positionOpenedAt(position) ? (
                      <>
                        <span aria-hidden="true" style={{ margin: '0 5px', opacity: 0.45 }}>·</span>
                        <span>Opened {formatOpenedTimestamp(positionOpenedAt(position))}</span>
                      </>
                    ) : null}
                  </div>
                </>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (isLoading) {
    return (
      <Card className="mb-3" bg="dark" text="white">
        <Card.Header 
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h5 className="mb-0">Positions</h5>
          <div>
            {isCollapsed ? <ChevronRight /> : <ChevronDown />}
          </div>
        </Card.Header>
        {!isCollapsed && (
          <Card.Body style={{ padding: 0 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '120px',
              width: '100%',
              border: 'none',
              outline: 'none'
            }}>
              <LoadingSpinner />
            </div>
          </Card.Body>
        )}
      </Card>
    );
  }

  // Connect Trade streams are incremental only — REST (or balances seed) is the initial snapshot.
  // Unblock as soon as we have rows OR the dedicated positions GET has settled.
  const awaitingInitialPositions = positionsLoading && (!positions || positions.length === 0);

  const emptyOrLoadingBlock = awaitingInitialPositions ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
        width: '100%',
        border: 'none',
        outline: 'none',
      }}
    >
      <LoadingSpinner />
    </div>
  ) : (
    <p className="text-center py-3">No open positions.</p>
  );

  const positionsTable = (
    <Table
      bordered={!embeddedSurface}
      hover
      size="sm"
      className={embeddedSurface ? 'positions-table positions-table--embedded' : 'positions-table'}
      style={{
        fontSize: '0.8rem',
        margin: renderAsChild ? 0 : undefined,
        backgroundColor: colors.bgPrimary,
        color: colors.text,
        '--bs-table-bg': embeddedSurface ? colors.bgSecondary : colors.bgPrimary,
        '--bs-table-hover-bg': embeddedSurface ? colors.bgHeader : colors.bgSecondary,
        '--bs-table-border-color': colors.border,
      }}
    >
      <thead>
        <tr>
          {columnOrder.map((key, idx) => renderHeaderCell(key, idx))}
        </tr>
      </thead>
      <tbody>
        {sortedPositions.map((position, index) => {
          const correctUnrealizedPL = calculateUnrealizedPL(position);
          const correctUnrealizedPLPercent = calculateUnrealizedPLPercent(position);
          const marketValue = calculateMarketValue(position);
          const accountPercentage = calculateAccountPercentage(position);
          const computed = { correctUnrealizedPL, correctUnrealizedPLPercent, marketValue, accountPercentage };
          const uniqueKey = positionCardKey(position, index);
          return (
            <tr key={uniqueKey}>
              {columnOrder.map((key) => renderBodyCell(key, position, computed))}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const tableContentInner = (
    <>
      {isMobile && showMobileSectionTitle ? renderMobileSectionHeader() : null}
      {showPresentationControls ? renderMobileToolbar() : null}
      {!positions || positions.length === 0 ? (
        <div
          className="table-responsive"
          style={embeddedSurface ? { padding: `12px ${embeddedCardInsetX}px` } : undefined}
        >
          {emptyOrLoadingBlock}
        </div>
      ) : showPresentationControls && mobileLayout === 'compact' ? (
        renderCompactPositionCards()
      ) : (
        <div className={pinColumnHeader ? 'positions-table-scroll' : 'table-responsive'}>
          {positionsTable}
        </div>
      )}
    </>
  );

  const tableContent = embeddedSurface ? (
    <div style={embeddedCardShellStyle}>{tableContentInner}</div>
  ) : (
    tableContentInner
  );

  const columnsSheet = showPresentationControls ? (
    <BottomSheet
      show={columnsSheetOpen}
      onHide={() => setColumnsSheetOpen(false)}
      title="Columns"
      testId="positions-columns-sheet"
    >
      <SheetOptionList
        multi
        ariaLabel="Optional position columns"
        testId="positions-columns-options"
        value={showRsColumn ? ['RelativeStrength'] : []}
        onChange={(key) => {
          if (key === 'RelativeStrength') toggleRelativeStrengthColumn();
        }}
        getTestId={(option) => `positions-column-${option.value}`}
        options={[
          {
            value: 'RelativeStrength',
            label: 'Relative Strength',
            hint: 'Score vs SPY on the table and position cards',
          },
        ]}
      />
    </BottomSheet>
  ) : null;

  if (renderAsChild) {
    return (
      <>
        {tableContent}
        {columnsSheet}
      </>
    );
  }

  return (
    <>
      <Card className="mb-3" bg="dark" text="white">
        <Card.Header 
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h5 className="mb-0">Positions</h5>
          <div>
            {isCollapsed ? <ChevronRight /> : <ChevronDown />}
          </div>
        </Card.Header>
        
        {!isCollapsed && (
          <Card.Body style={{ padding: 0 }}>
            {tableContent}
          </Card.Body>
        )}
      </Card>
      {columnsSheet}
    </>
  );
};

export default PositionsTable; 